import WarningIcon from '@mui/icons-material/Warning';
import { Alert } from '@mui/material';
import { useIntl } from 'react-intl';

import type { StaleCategory } from '../api/types';
import type { ResultsStaleness } from '../types';

interface TablesStaleBannerProps {
  staleness: ResultsStaleness;
}

/** The order the categories are named in, whatever order the API sends them. */
const CATEGORY_ORDER: readonly StaleCategory[] = ['definition', 'cohorts', 'variables'];

/** One message per combination of changed categories, so each sentence reads correctly as written. */
const MESSAGE_IDS: Record<string, string> = {
  definition: 'analysisDetail.tables.stale.definition',
  cohorts: 'analysisDetail.tables.stale.cohorts',
  variables: 'analysisDetail.tables.stale.variables',
  'cohorts+variables': 'analysisDetail.tables.stale.cohortsAndVariables',
  'definition+cohorts': 'analysisDetail.tables.stale.definitionAndCohorts',
  'definition+variables': 'analysisDetail.tables.stale.definitionAndVariables',
  'definition+cohorts+variables': 'analysisDetail.tables.stale.definitionCohortsAndVariables',
};

const FALLBACK_MESSAGE_ID = 'analysisDetail.tables.stale.fallback';

/**
 * The message for the changed categories. A stale run with no recognised category gets the generic
 * message, so an unknown category from a newer API never hides the banner.
 */
const messageIdFor = (categories: readonly StaleCategory[]): string => {
  const key = CATEGORY_ORDER.filter((category) => categories.includes(category)).join('+');
  return MESSAGE_IDS[key] ?? FALLBACK_MESSAGE_ID;
};

export const TablesStaleBanner = ({ staleness }: TablesStaleBannerProps) => {
  const { formatMessage } = useIntl();

  return (
    <Alert
      severity="warning"
      icon={<WarningIcon fontSize="inherit" />}
      sx={{ py: 0.5 }}
      data-testid="analysis-results-stale-banner"
    >
      {formatMessage({ id: messageIdFor(staleness.categories) })}
    </Alert>
  );
};
